// Per-field business-address validation state, keyed by `fieldKey`, so a page that
// captures both a Corporate Legal Address and a Mailing Address tracks each one
// independently. See actions/businessAddress.ts for why this is separate from the
// single-slot personAddressOptions used by the employee flow.

interface FieldState {
  responsePayload: any;
  loading: boolean;
  hasErrors: boolean;
  errorPayload: any;
  isValidated: boolean;
  // Normalized key of the address that was validated; used to tell whether the
  // address currently on the page is still the validated one (carry-forward
  // across pages that share a fieldKey, re-ask only on a real edit).
  validatedKey: string | null;
}

const EMPTY_FIELD: FieldState = {
  responsePayload: null,
  loading: false,
  hasErrors: false,
  errorPayload: null,
  isValidated: false,
  validatedKey: null,
};

const INITIAL_STATE: { [fieldKey: string]: FieldState } = {};

const businessAddressReducer = (
  state: { [fieldKey: string]: FieldState } = INITIAL_STATE,
  action: any
): { [fieldKey: string]: FieldState } => {
  switch (action.type) {
    case "UPDATE_BUSINESS_ADDRESS_OPTIONS": {
      const { fieldKey, ...rest } = action.data;
      return {
        ...state,
        [fieldKey]: { ...EMPTY_FIELD, ...state[fieldKey], ...rest },
      };
    }
    case "UPDATE_BUSINESS_ADDRESS_VALIDATED_FLAG": {
      const { fieldKey, isValidated, validatedKey } = action.data;
      return {
        ...state,
        [fieldKey]: {
          ...EMPTY_FIELD,
          ...state[fieldKey],
          isValidated,
          validatedKey:
            validatedKey !== undefined
              ? validatedKey
              : state[fieldKey]?.validatedKey ?? null,
        },
      };
    }
    case "CLEAR_BUSINESS_ADDRESS_SUGGESTIONS": {
      const { fieldKey } = action.data;
      return {
        ...state,
        [fieldKey]: {
          ...EMPTY_FIELD,
          ...state[fieldKey],
          responsePayload: null,
          loading: false,
          hasErrors: false,
          errorPayload: null,
        },
      };
    }
    default:
      return state;
  }
};

export default businessAddressReducer;
